'use client';

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { trackLeadCapture } from '@/components/Analytics';
import { LEAD_DOWNLOADS_ID } from '@/lib/scroll-targets';

/**
 * The status line shown after someone opens the "Confirm my email" link — and
 * the place the lead conversion is actually reported.
 *
 * WHY THE CONVERSION LIVES HERE AND NOT ON THE FORM
 * The magic-link gate that used to guard these pages was quietly doing two
 * jobs: it proved the address was real (you had to open the inbox), and it
 * signed you in. It was removed as a sign-in wall, which took the proof with
 * it. Reporting a conversion the moment someone types an address would then
 * feed Smart Bidding a signal that anyone can mint — optimising the account
 * toward whoever fills forms fastest, which is the opposite of the point.
 *
 * `?confirm=confirmed` is set by app/api/lead/verify, and only on the FIRST
 * verification of an address, so a forwarded or prefetched link cannot inflate
 * the count. `already` means a repeat click: say the right thing, report
 * nothing.
 *
 * WHY IT SCROLLS
 * Confirming lands you at the top of a long marketing page with the thing you
 * came for several screens down, so the click reads as "nothing happened". The
 * banner alone does not fix that — it is deliberately small. The scroll is what
 * answers the question.
 *
 * Two things make this harder than one scrollIntoView call, and both were
 * observed rather than guessed:
 *   - the target renders only after each page's session probe resolves, so it
 *     does not exist on mount;
 *   - even once it exists, the page is still growing. On /pitch-deck-templates
 *     the deck previews land late, and a scroll fired before they do lands on a
 *     target sitting a few hundred pixels down that afterwards sits at ~3900 —
 *     i.e. it silently does nothing, which is exactly the "wonder what
 *     happened" this is meant to fix.
 * So it waits for the target's position to stop moving, then scrolls once.
 */

/** Give the async gate a few seconds to appear, then stop looking. */
const SCROLL_TIMEOUT_MS = 5000;
const SCROLL_POLL_MS = 100;

interface LeadConfirmedProps {
  /**
   * What the confirmation just unlocked. /match-preview unlocks an uploader
   * rather than a file, and telling someone their download is below when it is
   * not is the same class of lie as the stale copy this flow has already been
   * bitten by twice. A fixed choice, not free text, so each option has a
   * translation instead of a sentence assembled from English fragments.
   */
  unlocked?: 'download' | 'uploader';
}

export function LeadConfirmed({ unlocked = 'download' }: LeadConfirmedProps = {}) {
  const { t } = useTranslation();
  // English is the fallback for every key, so a missing translation renders English.
  const s = (key: string, fallback: string, options?: Record<string, unknown>): string =>
    t(`pageContent.leadCapture.confirmed.${key}`, { defaultValue: fallback, ...options });
  const [confirm, setConfirm] = useState<string | null>(null);
  // React may run effects twice in development; the ref keeps one click from
  // reporting two conversions.
  const reported = useRef(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Read the param off window rather than with useSearchParams(): these three
    // landing pages are statically prerendered, and that hook would opt every
    // one of them out of static rendering. The same trade-off is already
    // documented in FreeVcListContent for ?auth=required.
    if (typeof window === 'undefined') return;
    const value = new URLSearchParams(window.location.search).get('confirm');
    setConfirm(value);

    if (value === 'confirmed' && !reported.current) {
      reported.current = true;
      trackLeadCapture();
    }

    // Only the two success states have somewhere to go. `expired` and `error`
    // leave the visitor at the top, where the form they need to resubmit is.
    if (value !== 'confirmed' && value !== 'already') return;

    let elapsed = 0;
    let last: { offset: number; height: number } | null = null;

    const timer = window.setInterval(() => {
      elapsed += SCROLL_POLL_MS;
      const target = document.getElementById(LEAD_DOWNLOADS_ID);

      if (target) {
        const rect = target.getBoundingClientRect();
        const offset = rect.top + window.scrollY;
        const height = rect.height;
        // Scroll only once the target has stopped both MOVING and RESIZING
        // between ticks. Either one still changing means centring it now aims at
        // a layout the page is about to replace — which looks exactly like not
        // scrolling at all.
        if (last !== null && Math.abs(offset - last.offset) < 2 && Math.abs(height - last.height) < 2) {
          window.clearInterval(timer);
          const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          // 'start', not 'center'. On /match-preview the target wraps the whole
          // tool section, and centring a block taller than the viewport puts its
          // middle on screen — which can mean not scrolling at all. 'start'
          // means the same thing on every page ("take me to this section"), and
          // the scroll-mt-24 each target carries clears the sticky header.
          target.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
          return;
        }
        last = { offset, height };
      }

      if (elapsed >= SCROLL_TIMEOUT_MS) {
        window.clearInterval(timer);
        // Out of patience but the target is there: an unsettled page is still
        // better landed on than the top of the hero.
        document.getElementById(LEAD_DOWNLOADS_ID)?.scrollIntoView({ block: 'start' });
      }
    }, SCROLL_POLL_MS);

    return () => window.clearInterval(timer);
  }, []);

  if (!confirm || dismissed) return null;

  const isProblem = confirm === 'expired' || confirm === 'error';

  // Same words for `confirmed` and `already`, deliberately. Those two differ
  // only in whether the conversion fires — a server-side concern the visitor has
  // no reason to read about. Telling someone who just did what was asked that
  // they were "already confirmed" reads as a correction, when the honest answer
  // to their click is simply yes.
  const message =
    confirm === 'confirmed' || confirm === 'already'
      ? s('ok', 'Email confirmed — {{unlocked}} is below.', {
          unlocked:
            unlocked === 'uploader'
              ? s('uploader', 'the uploader')
              : s('download', 'your download'),
        })
      : confirm === 'expired'
        ? s('expired', 'That confirmation link has expired. Send yourself a fresh one below.')
        : s('error', 'We could not confirm that link just now. Try it again in a moment.');

  // Layout-neutral on purpose: this mounts as a top-level sibling on
  // /free-vc-list and /pitch-deck-templates but INSIDE an existing
  // `container max-w-3xl` section on /match-preview. A `container` here nested
  // a container inside a container there, and carried no bottom spacing at all,
  // so the pill sat flush against the box below it.
  return (
    <div className="mx-auto w-full max-w-xl px-4 pt-4 pb-6">
      <div
        role="status"
        className={`flex items-center justify-center gap-2 rounded-full border px-4 py-1.5 text-center text-xs ${
          isProblem
            ? 'border-amber-200 bg-amber-50 text-amber-800'
            : 'border-green-200 bg-green-50 text-green-800'
        }`}
      >
        <span>{message}</span>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          aria-label={s('dismiss', 'Dismiss')}
          className="shrink-0 leading-none opacity-50 transition-opacity hover:opacity-100"
        >
          &times;
        </button>
      </div>
    </div>
  );
}
